import apiClient from './AuthService';

// Types based on backend API
export type DayType = 'WORKING' | 'HOLIDAY';

export interface SpatialExperienceChallenge {
  spatial_experience_challenge_id?: string;
  project_id: string;
  user_id?: string;
  member_id: string;
  member_name?: string;
  scene_number: number;
  scene_title?: string | null;
  scene_text?: string | null;
  scene_comments?: string | null;
  positions?: string | null;
  day_type: DayType;
  created_at?: string;
  updated_at?: string;
}

// Interface for avatar positions stored in the positions field
export interface AvatarPosition {
  memberId: string;
  memberName: string;
  x: number;  // percentage 0-100
  y: number;  // percentage 0-100
  color: string;
}

export interface SpatialExperienceChallengeResponse {
  spatial_experience_challenge_id: string;
  message: string;
}

export interface SpatialExperienceChallengeData {
  data: SpatialExperienceChallenge | SpatialExperienceChallenge[];
}

// Create a new challenge entry
export const saveSpatialExperienceChallenge = async (
  projectId: string,
  memberId: string,
  sceneNumber: number,
  dayType: DayType,
  sceneTitle?: string,
  sceneText?: string,
  sceneComments?: string,
  positions?: string
): Promise<SpatialExperienceChallengeResponse> => {
  try {
    const requestData = {
      project_id: projectId,
      member_id: memberId,
      scene_number: sceneNumber,
      day_type: dayType,
      scene_title: sceneTitle || null,
      scene_text: sceneText || null,
      scene_comments: sceneComments || null,
      positions: positions || null
    };

    console.log('📤 Sending SpatialExperience data:', requestData);

    const response = await apiClient.post<SpatialExperienceChallengeResponse>(
      '/quiz/spatial-experience-challenge',
      requestData
    );

    console.log('✅ SpatialExperience saved successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ SpatialExperience API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message && typeof error.response.data.message === 'string') {
      throw new Error(error.response.data.message);
    } else if (error.response?.data && typeof error.response.data === 'string') {
      throw new Error(error.response.data);
    }

    throw new Error('Error al guardar el challenge. Por favor, intenta de nuevo.');
  }
};

// Get a single challenge by ID
export const getSpatialExperienceChallenge = async (
  spatialExperienceChallengeId: string
): Promise<SpatialExperienceChallenge> => {
  try {
    const response = await apiClient.get<SpatialExperienceChallengeData>(
      `/quiz/spatial-experience-challenge/${spatialExperienceChallengeId}`
    );
    return response.data.data as SpatialExperienceChallenge;
  } catch (error: any) {
    console.error('❌ getSpatialExperienceChallenge error:', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Get all challenges for a project
export const getProjectSpatialExperienceChallenges = async (
  projectId: string
): Promise<SpatialExperienceChallenge[]> => {
  try {
    console.log('📡 Fetching spatial experience challenges for project:', projectId);
    const response = await apiClient.get<SpatialExperienceChallengeData>(
      `/quiz/project/${projectId}/spatial-experience-challenges`
    );

    const data = response.data.data;
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }
    return data;
  } catch (error: any) {
    console.error('❌ getProjectSpatialExperienceChallenges error:', error);

    if (error.response?.status === 404) {
      console.log('📡 No challenges found for project (404), returning empty array');
      return [];
    }

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Get all challenges for a member
export const getMemberSpatialExperienceChallenges = async (
  memberId: string
): Promise<SpatialExperienceChallenge[]> => {
  try {
    console.log('📡 Fetching spatial experience challenges for member:', memberId);
    const response = await apiClient.get<SpatialExperienceChallengeData>(
      `/quiz/member/${memberId}/spatial-experience-challenges`
    );
    console.log('📡 Response received:', response.data);

    const data = response.data.data;
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }
    return data;
  } catch (error: any) {
    console.error('❌ getMemberSpatialExperienceChallenges error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.status === 404) {
      console.log('📡 No challenges found for member (404), returning empty array');
      return [];
    }

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Helper function to get challenges filtered by day type
export const getMemberChallengesByDayType = async (
  memberId: string,
  dayType: DayType
): Promise<SpatialExperienceChallenge[]> => {
  const challenges = await getMemberSpatialExperienceChallenges(memberId);
  return challenges.filter(c => c.day_type === dayType);
};
